import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/lib/auth";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card } from "@/components/ui/card";
import { toast } from "sonner";
import { Activity, Loader2 } from "lucide-react";
import api from "@/lib/api";

export default function Login() {
  const { login, user } = useAuth();
  const nav = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    // Best-effort seed so first login has demo data.
    api.post("/dev/seed").catch(() => {});
    if (user) nav("/", { replace: true });
  }, [user, nav]);

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await login(email, password);
      toast.success("Welcome back");
      nav("/", { replace: true });
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Login failed");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="min-h-screen w-full flex items-center justify-center bg-[#09090b] px-4">
      <div className="absolute inset-0 opacity-40 pointer-events-none"
        style={{ background: "radial-gradient(600px circle at 20% 20%, rgba(99,102,241,0.15), transparent 40%), radial-gradient(500px circle at 80% 70%, rgba(16,185,129,0.08), transparent 40%)" }} />
      <Card className="relative w-full max-w-md p-8 bg-[#121215] border-[#27272a]">
        <div className="flex items-center gap-3 mb-6">
          <div className="w-10 h-10 rounded-lg bg-indigo-500/15 border border-indigo-500/30 flex items-center justify-center">
            <Activity className="w-5 h-5 text-indigo-400" />
          </div>
          <div>
            <div className="text-xs uppercase tracking-widest text-zinc-500">EOD Ops</div>
            <div className="font-display text-lg font-semibold">Report Automation</div>
          </div>
        </div>
        <h1 className="font-display text-2xl font-bold mb-1">Sign in</h1>
        <p className="text-sm text-zinc-400 mb-6">Track EOD submissions across your team.</p>
        <form onSubmit={submit} className="space-y-4">
          <div>
            <Label htmlFor="email" className="text-xs uppercase tracking-wider text-zinc-400">Email</Label>
            <Input id="email" type="email" data-testid="login-email-input"
              value={email} onChange={(e) => setEmail(e.target.value)}
              className="mt-1.5 bg-[#0f0f12] border-[#27272a] h-11" required />
          </div>
          <div>
            <Label htmlFor="pw" className="text-xs uppercase tracking-wider text-zinc-400">Password</Label>
            <Input id="pw" type="password" data-testid="login-password-input"
              value={password} onChange={(e) => setPassword(e.target.value)}
              className="mt-1.5 bg-[#0f0f12] border-[#27272a] h-11" required />
          </div>
          <Button type="submit" data-testid="login-submit-button" disabled={busy}
            className="w-full h-11 bg-indigo-500 hover:bg-indigo-600 text-white font-medium">
            {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : "Sign in"}
          </Button>
        </form>
      </Card>
    </div>
  );
}
